import { DiceVisibilities, RollTos } from "@/constants";
import { Context } from "@/context";
import { postNewSettings } from "@/helpers/postNewSettings";
import { useDiceRollPrivacyStore } from "@/hooks/useDiceRollPrivacyStore";
import { useShared3dDiceSettingStore } from "@/hooks/useShared3dDiceSettingStore";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type {
  DiceRollEventPayload,
  DiceSet,
  RollDicePayload,
  RollTo,
} from "@/types";
import {
  lazy,
  Suspense,
  useContext,
  useEffect,
  type FC,
  type MouseEvent,
} from "react";

export const rollerImport = () =>
  import("./components/Roller").then(({ Roller }) => ({
    default: Roller,
  }));

const Roller = lazy(rollerImport);

export interface CustomDiceRollerProps {
  onClose: () => void;
  rollInstrumentationCallback?: (payload: DiceRollEventPayload) => void;
  rollCallback?: (payload: RollDicePayload) => void;
  rollErrorCallback?: (exception: unknown) => void;
  setId?: string;
  clearDiceCallback?: () => void;
  rollToCallback?: (e: MouseEvent<HTMLButtonElement>) => void;
  diceSets: DiceSet[];
  gameId?: number;
  preview?: boolean;
  customDiceSet?: boolean;
  visible?: boolean;
  entityType?: string;
  entityId?: string;
  name?: string;
  dmId?: string;
}

export const CustomDiceRoller: FC<CustomDiceRollerProps> = ({
  visible,
  ...rest
}) => {
  const { user, nodeEnv, env } = useContext(Context);
  const { getIsShared3dDiceSettingEnabled, setIsShared3dDiceSettingEnabled } =
    useShared3dDiceSettingStore();
  const { getDiceRollPrivacy, setHasSeenDiceContainer, setRollToPrivacy } =
    useDiceRollPrivacyStore();
  const isUserIdNumber = !isNaN(Number(user?.id));
  const { setUserDiceData } = useUserDiceDataStore();
  const userDiceData = useUserDiceDataStore((state) =>
    state.getUserDiceData(Number(user?.id)),
  );

  useEffect(() => {
    if (
      isUserIdNumber &&
      getIsShared3dDiceSettingEnabled(Number(user?.id)) === undefined
    ) {
      setIsShared3dDiceSettingEnabled(Number(user?.id));
    }
  }, [
    getIsShared3dDiceSettingEnabled,
    isUserIdNumber,
    setIsShared3dDiceSettingEnabled,
    user?.id,
  ]);

  useEffect(() => {
    (async () => {
      if (userDiceData && !userDiceData.settings.visibility) {
        const newUserData = {
          ...userDiceData,
          settings: {
            ...userDiceData.settings,
            visibility:
              getIsShared3dDiceSettingEnabled(Number(user?.id)) === false
                ? DiceVisibilities.Disabled
                : DiceVisibilities.Shared,
          },
        };

        setUserDiceData(Number(user?.id), newUserData);
        await postNewSettings(newUserData.settings, nodeEnv!, env!);
      }
    })();
  }, [
    env,
    getIsShared3dDiceSettingEnabled,
    nodeEnv,
    setUserDiceData,
    user?.id,
    userDiceData,
  ]);

  useEffect(() => {
    const diceContainerConfigJson = JSON.parse(
      localStorage.getItem("diceContainerConfig") || "{}",
    );
    const diceContainerConfigUserSettings =
      diceContainerConfigJson[Number(user?.id)];

    // migrate old dice container privacy settings
    if (diceContainerConfigUserSettings) {
      if (diceContainerConfigUserSettings.hasSeenDiceContainer) {
        setHasSeenDiceContainer(Number(user?.id));
      }

      if (diceContainerConfigUserSettings.rollTo) {
        setRollToPrivacy(
          Number(user?.id),
          diceContainerConfigUserSettings.rollTo,
        );
      }

      // remove old settings after migration
      delete diceContainerConfigJson[Number(user?.id)];

      const userConfigsCount = Object.keys(diceContainerConfigJson).length;

      if (userConfigsCount) {
        localStorage.setItem(
          "diceContainerConfig",
          JSON.stringify(diceContainerConfigJson),
        );
      } else {
        localStorage.removeItem("diceContainerConfig");
      }
    }

    if (isUserIdNumber) {
      if (!getDiceRollPrivacy(Number(user?.id))) {
        setRollToPrivacy(Number(user?.id));
      } else if (
        getDiceRollPrivacy(Number(user?.id))?.rollTo === ("All" as RollTo)
      ) {
        setRollToPrivacy(Number(user?.id), RollTos.Everyone);
      }
    }
  }, [
    getDiceRollPrivacy,
    isUserIdNumber,
    setHasSeenDiceContainer,
    setRollToPrivacy,
    user,
  ]);

  if (!visible) return null;

  return (
    <Suspense>
      <Roller {...rest} />
    </Suspense>
  );
};
